import React from "react"
import { ActionType, StepAction } from "../types/tutorial-config"
import SelectorPicker from "./selector-picker"
import { newAction } from "./empty-config"

const ACTION_TYPES: ActionType[] = [
  "hideElement",
  "showElement",
  "setStyle",
  "addClass",
  "removeClass",
  "click",
  "setPointerEvents",
  "scrollIntoView",
  "clearSearch",
  "disableButton",
  "enableButton",
  "setValue",
  "expandTreePath",
  "selectTreeNode",
  "expandNode",
]

const VALUE_LABEL: Partial<Record<ActionType, string>> = {
  setStyle: "Inline style (e.g. 'display:none')",
  addClass: "Class name to add",
  removeClass: "Class name to remove",
  setPointerEvents: "Pointer-events (e.g. 'none', 'auto')",
  setValue: "Value to set",
}

const NEEDS_TREE_PATH: ActionType[] = ["expandTreePath", "selectTreeNode"]

type Props = {
  actions: StepAction[] | undefined
  onChange: (actions: StepAction[]) => void
  treeMappingNames: string[]
  label: string
}

const StepActionEditor: React.FC<Props> = ({
  actions = [],
  onChange,
  treeMappingNames,
  label,
}) => {
  const update = (i: number, patch: Partial<StepAction>) => {
    const next = actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a))
    onChange(next)
  }
  const remove = (i: number) => onChange(actions.filter((_, idx) => idx !== i))
  const add = () => onChange([...actions, newAction("hideElement")])

  return (
    <div className="border rounded p-2 bg-gray-50">
      <div className="flex justify-between items-center mb-1">
        <span className="font-medium text-sm">{label}</span>
        <button
          type="button"
          onClick={add}
          className="text-xs px-2 py-0.5 bg-indigo-600 text-white rounded">
          + Add action
        </button>
      </div>
      {actions.length === 0 && (
        <div className="text-xs text-gray-500 italic">No actions.</div>
      )}
      {actions.map((a, i) => (
        <div key={i} className="border bg-white rounded p-2 mb-1">
          <div className="flex items-center gap-2">
            <select
              value={a.action}
              onChange={(e) =>
                update(i, { action: e.target.value as ActionType })
              }
              className="border rounded px-1 py-0.5 text-sm">
              {ACTION_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-xs text-red-600 ml-auto">
              ✕
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <label className="text-xs flex flex-col">
              Target
              <SelectorPicker
                value={a.target}
                onChange={(v) => update(i, { target: v })}
                treeNodeMappingNames={treeMappingNames}
              />
            </label>
            {VALUE_LABEL[a.action] !== undefined && (
              <label className="text-xs flex flex-col">
                {VALUE_LABEL[a.action]}
                <input
                  type="text"
                  value={a.value ?? ""}
                  onChange={(e) => update(i, { value: e.target.value })}
                  className="border rounded px-1 py-0.5 text-sm font-mono"
                />
              </label>
            )}
            {NEEDS_TREE_PATH.includes(a.action) && (
              <label className="text-xs flex flex-col col-span-2">
                Tree path (one segment per line)
                <textarea
                  rows={3}
                  value={(a.treePath ?? []).join("\n")}
                  onChange={(e) =>
                    update(i, {
                      treePath: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  className="border rounded px-1 py-0.5 text-sm font-mono"
                />
              </label>
            )}
            <label className="text-xs flex flex-col">
              Delay (ms, optional)
              <input
                type="number"
                value={a.delay ?? ""}
                onChange={(e) => {
                  const v = e.target.value
                  update(i, { delay: v === "" ? undefined : Number(v) })
                }}
                className="border rounded px-1 py-0.5 text-sm font-mono"
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  )
}

export default StepActionEditor
